import PropTypes from 'prop-types';
import { format, parse } from 'date-fns'; // Import date-fns functions
import { Card, CardContent } from '@shared/forms/FormStyles';

const EducationSection = ({ education }) => {

  // Helper function to format YYYY-MM dates for display
  const formatDateForDisplay = (dateString) => {
    if (!dateString || typeof dateString !== 'string' || !dateString.match(/^\d{4}-\d{2}$/)) {
      return dateString; // Return original if invalid or not in expected format
    }
    try {
      const date = parse(dateString, 'yyyy-MM', new Date());
      const utcDate = new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
      return format(utcDate, 'MMMM yyyy');
    } catch (error) {
      console.error("Error formatting date:", dateString, error);
      return dateString; // Return original on error
    }
  };
  if (!education || education.length === 0) {
    return null; // Don't render if no education data
  }

  return (
    <Card>
      <div className="border-b border-huntd-secondary/10 bg-white px-6 py-4">
        <h3 className="text-xl font-bold text-huntd-primary">Education</h3>
      </div>
      <CardContent className="space-y-4">
        {education.map((edu, index) => (
          <div key={`edu-${index}`} className="border-b border-huntd-secondary/10 pb-4 last:border-b-0 last:pb-0">
            <h4 className="font-medium text-huntd-primary">{edu.degree || 'Degree'} - {edu.fieldOfStudy || 'Field of Study'}</h4>
            <p className="text-sm text-huntd-secondary">{edu.institution}</p>
            <p className="text-sm text-huntd-secondary">
              {formatDateForDisplay(edu.startDate) || 'Start Date Missing'} - {edu.endDate ? formatDateForDisplay(edu.endDate) : 'Present'}
            </p>
            {edu.description ? <p className="mt-2 text-sm text-huntd-secondary/80">{edu.description}</p> : null}
            {/* Technical Skills Display */}
            {edu.technicalSkills?.length > 0 ? (
              <div className="mt-2">
                <h5 className="text-xs font-semibold text-huntd-primary uppercase tracking-wide mb-1">Technical Skills</h5>
                <div className="flex flex-wrap gap-2">
                  {edu.technicalSkills.map((skill, i) => (
                    <span key={`edu-tech-skill-${i}`} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            ) : null}
            {/* Soft Skills Display */}
            {edu.softSkills?.length > 0 ? (
              <div className="mt-2">
                <h5 className="text-xs font-semibold text-huntd-primary uppercase tracking-wide mb-1">Soft Skills</h5>
                <div className="flex flex-wrap gap-2">
                  {edu.softSkills.map((skill, i) => (
                    <span key={`edu-soft-skill-${i}`} className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            ) : null}
            {/* Industry Knowledge Display */}
            {edu.industryKnowledge?.length > 0 ? (
              <div className="mt-2">
                <h5 className="text-xs font-semibold text-huntd-primary uppercase tracking-wide mb-1">Industry Knowledge</h5>
                <div className="flex flex-wrap gap-2">
                  {edu.industryKnowledge.map((skill, i) => (
                    <span key={`edu-ind-skill-${i}`} className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full">
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            ) : null}
            {/* TODO: Add edit functionality if needed */}
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

EducationSection.propTypes = {
  education: PropTypes.arrayOf(PropTypes.shape({
    institution: PropTypes.string,
    degree: PropTypes.string,
    fieldOfStudy: PropTypes.string,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    description: PropTypes.string,
    // skills removed, replaced by categorized skills below
    technicalSkills: PropTypes.arrayOf(PropTypes.string),
    softSkills: PropTypes.arrayOf(PropTypes.string),
    industryKnowledge: PropTypes.arrayOf(PropTypes.string)
  }))
};

export default EducationSection;